<template>
    <div>
        <div v-if="showLoadingModal" class="loading-modal">
            <div class="loading-content">
                <div class="spinner"></div>
                <p>{{ $t('SCHOOL_CONTACT_FORM.FORM.LOADING_MESSAGE') }}</p>
            </div>
        </div>
        <form class="contact--form" @submit.prevent>
        <fieldset class="form-section" v-if="!enrollment || !contractType" role="group" aria-labelledby="section-enrollment-select">
            <legend id="section-enrollment-select" class="section-title cursor-pointer" @click.prevent="toggleSection('enrollmentSelect')">
                <fluent-icon :icon="collapsed.enrollmentSelect ? 'chevron-right' : 'chevron-down'" size="16" />
                {{ $t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENTS.LABEL') }}
            </legend>
            <div class="row items-end" v-show="!collapsed.enrollmentSelect">
                <div class="small-12 medium-9 columns">
                    <label>
                        <span class="label-text">{{ $t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENTS.LABEL') }}</span>
                    </label>
                    <multiselect
                        v-model="enrollment"
                        track-by='id'
                        label="student_name"
                        :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENTS.PLACEHOLDER')"
                        selected-label=""
                        select-label=""
                        deselect-label=""
                        :max-height="160"
                        :options="enrollments"
                        :allow-empty="true"
                        :option-height="104"
                        @select="checkEnrollment"
                    >
                        <template slot="option" slot-scope="{ option }">
                            <div
                                class="flex items-center justify-between w-full"
                                v-tooltip.right-start="{
                                    content: 'Aluno: ' + option.student_name + ' • Ano: ' + (option.school_year || '-'),
                                    delay: { show: 250, hide: 0 },
                                    hideOnTargetClick: false,
                                    autoHide: true,
                                    classes: 'tooltip-noninteractive',
                                }"
                            >
                                <span class="label-text">{{ option.student_name }}</span>
                                <span v-if="option && option.school_year" :class="getYearBadgeClass(option.school_year)">{{ option.school_year }}</span>
                            </div>
                        </template>
                        </multiselect>
                </div>

            </div>
        </fieldset>

        <div v-if="enrollment && contractType==='Digital'">
            <div class="medium-12 columns enrollment-title">
                <div class="flex flex-col gap-1">
                    <span class="page-sub-title section-title text-base font-semibold">
                        {{ `${$t('SCHOOL_CONTACT_FORM.FORM.CONTRACT.TITLE')}` }}
                    </span>
                    <div class="medium-12 row gap-1 items-center mt-1">
                        <span class="page-sub-title text-base font-semibold">
                            {{ `${enrollment.student_name}` }}
                        </span>
                        <span
                            v-if="enrollment && enrollment.school_year"
                            :class="getYearBadgeClass(enrollment.school_year)"
                            v-tooltip.right-start="{
                                content: 'Aluno: ' + enrollment.student_name + ' • Ano: ' + enrollment.school_year,
                                delay: { show: 300, hide: 0 },
                                hideOnClick: true,
                            }"
                        >
                            {{ enrollment.school_year }}
                        </span>
                    </div>
                </div>
            </div>

            <fieldset class="form-section" role="group" aria-labelledby="section-financial">
                <legend id="section-financial" class="section-title cursor-pointer" @click.prevent="toggleSection('financial')">
                    <fluent-icon :icon="collapsed.financial ? 'chevron-right' : 'chevron-down'" size="16" />
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.ANNUITY.LABEL') }}
                </legend>
                <div v-show="!collapsed.financial">
                    <div class="grid-2">
                        <label :class="{ error: $v.annuity.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.ANNUITY.LABEL') }}
                            <input v-model.trim="annuity" type="text" :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.ANNUITY.PLACEHOLDER')"
                                @input="$v.annuity.$touch" v-mask="moneyMask" />
                            <span v-if="$v.annuity.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.ANNUITY.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <div class="row" :class="{ error: $v.installmentsMaturity.$error }">
                            <label>
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_MATURITY.LABEL') }}
                            </label>
                            <multiselect v-model="installmentsMaturity"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_MATURITY.PLACEHOLDER')" selected-label
                                :select-label="$t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_MATURITY.SELECT_PLACEHOLDER')"
                                :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_MATURITY.REMOVE')" :max-height="160"
                                :options="installmentMaturityOptions" :allow-empty="true" :option-height="104" @input="$v.installmentsMaturity.$touch" />
                            <span v-if="$v.installmentsMaturity.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_MATURITY.ERROR_MESSAGE') }}
                            </span>
                        </div>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.installmentValue.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_VALUE.LABEL') }}
                            <input v-model.trim="installmentValue" type="text"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_VALUE.PLACEHOLDER')"
                                @input="$v.installmentValue.$touch" v-mask="moneyMask" disabled />
                            <span v-if="$v.installmentValue.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_VALUE.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label :class="{ error: $v.startDateInstallment.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.START_DATE_INSTALLMENT.LABEL') }}
                            <input v-model.trim="startDateInstallment" type="month" 
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.START_DATE_INSTALLMENT.PLACEHOLDER')"
                                @input="$v.startDateInstallment.$touch" :min="startDateMin" :max="startDateMax"/>
                            <span v-if="$v.startDateInstallment.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.START_DATE_INSTALLMENT.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <div class="row" :class="{ error: $v.remainingInstallmentsMaturity.$error }">
                            <label>
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.REMAINING_INSTALLMENTS_MATURITY.LABEL') }}
                            </label>
                            <multiselect v-model="remainingInstallmentsMaturity" :disabled="isEditingBlocked"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.REMAINING_INSTALLMENTS_MATURITY.PLACEHOLDER')" selected-label
                                :select-label="$t('SCHOOL_CONTACT_FORM.FORM.REMAINING_INSTALLMENTS_MATURITY.SELECT_PLACEHOLDER')"
                                :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.REMAINING_INSTALLMENTS_MATURITY.REMOVE')" :max-height="160"
                                :options="installmentMaturityOptions" :allow-empty="true" :option-height="104" @input="$v.remainingInstallmentsMaturity.$touch" />
                            <span v-if="$v.remainingInstallmentsMaturity.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.REMAINING_INSTALLMENTS_MATURITY.ERROR_MESSAGE') }}
                            </span>
                        </div>

                        <div class="row" :class="{ error: $v.installmentsQuantity.$error }">
                            <label>
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENTS_QUANTITY.LABEL') }}
                            </label>
                            <multiselect v-model="installmentsQuantity"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENTS_QUANTITY.PLACEHOLDER')" selected-label
                                :select-label="$t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENTS_QUANTITY.SELECT_PLACEHOLDER')"
                                :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENTS_QUANTITY.REMOVE')" :max-height="160"
                                :options="installmentsQuantityOptions" :allow-empty="true" :option-height="104" @input="$v.installmentsQuantity.$touch"/>
                            <span v-if="$v.installmentsQuantity.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENTS_QUANTITY.ERROR_MESSAGE') }}
                            </span>
                        </div>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.lastDayInstallment.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.LAST_DAY_INSTALLMENT.LABEL') }}
                            <input v-model.trim="lastDayInstallment" type="date"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.LAST_DAY_INSTALLMENT.PLACEHOLDER')"
                                @input="$v.lastDayInstallment.$touch" disabled/>
                            <span v-if="$v.lastDayInstallment.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.LAST_DAY_INSTALLMENT.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <div class="flex flex-col gap-2">
                            <label>
                                <input v-model.trim="punctualityDiscountIsPercentage" type="checkbox" />
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_IS_PERCENTAGE.LABEL') }}
                            </label>
                            <label v-if="punctualityDiscountIsPercentage" :class="{ error: $v.punctualityDiscountPercentage.$error }">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_PERCENTAGE.LABEL') }}
                                <input v-model.trim="punctualityDiscountPercentage" type="number"
                                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_PERCENTAGE.PLACEHOLDER')"
                                    @input="$v.punctualityDiscountPercentage.$touch" />
                                <span v-if="$v.punctualityDiscountValue.$error || $v.punctualityDiscountPercentage.$error" class="message">
                                    {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_PERCENTAGE.ERROR_MESSAGE') }}
                                </span>
                            </label>
                            <label v-else :class="{ error: $v.punctualityDiscountValue.$error }">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_VALUE.LABEL') }}
                                <input v-model.trim="punctualityDiscountValue" type="text"
                                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_VALUE.PLACEHOLDER')"
                                    @input="$v.punctualityDiscountValue.$touch" v-mask="moneyMask"/>
                                <span v-if="$v.punctualityDiscountValue.$error || $v.punctualityDiscountPercentage.$error" class="message">
                                    {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_VALUE.ERROR_MESSAGE') }}
                                </span>
                            </label>
                        </div>

                        <div v-if="currentSchoolData && currentSchoolData.enable_manual_contract" class="flex flex-col gap-2">
                            <label>
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_FILE.LABEL') }}
                            </label>
                            <div v-show="!collapsed.manual">
                                <div class="medium-12 row file-section-wrap">
                                    <label v-if="!contractFiles || contractFiles.length === 0" class="medium-12 file-section-empty-label">
                                            {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_FILE.EMPTY_LABEL') }}
                                    </label>
                                    <div v-for="(contractFile, index) in contractFiles" class="medium-12 row file-section-item" >
                                        <div v-if="contractFile && contractFile.data_url" class="medium-12 flex items-center justify-center">
                                            <attachment-box 
                                                :url="contractFile.data_url"
                                            />
                                        </div>
                                        <div class="medium-12 items-center flex justify-center">
                                            <woot-button
                                                v-if="contractFile && contractFile.data_url"
                                                icon="delete"
                                                color-scheme="alert"
                                                variant="smooth"
                                                size="small"
                                                :disabled="isEditingBlocked"
                                                @click.prevent="removeFile(contractFile.id)"
                                            >
                                                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_FILE.REMOVE_BUTTON_LABEL') }}
                                            </woot-button>
                                        </div>
                                    </div>
                                    <input type="file"
                                        ref="fileInput" 
                                        accept=".jpg, .jpeg, .png, .gif, .pdf, .doc, .docx"
                                        @input="handleAttachFile"
                                        style="display: none;"
                                    />
                                    <woot-button
                                        v-if = "!contractFiles || contractFiles.length < 5"
                                        class = "file-section-upload-button"
                                        icon="upload"
                                        color-scheme="primary"
                                        variant="smooth"
                                        size="small"
                                        :disabled="isEditingBlocked"
                                        @click.prevent="openFileInput()"
                                    >
                                        {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_FILE.ADD_BUTTON_LABEL') }}
                                    </woot-button>
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label>
                                <input v-model.trim="punctualityDiscountRuleEqualToDueDate" type="checkbox" />
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_RULE_EQUAL_TO_DUE_DATE.LABEL') }}
                            </label>
                            <label v-if="!punctualityDiscountRuleEqualToDueDate" :class="{ error: $v.contractPunctualityDiscountRule.$error }">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_RULE.LABEL') }}
                                <input v-model.trim="contractPunctualityDiscountRule" type="date" :max="startDateInstallment"
                                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_RULE.PLACEHOLDER')"
                                    @input="$v.contractPunctualityDiscountRule.$touch" />
                                <span v-if="$v.contractPunctualityDiscountRule.$error" class="message">
                                    {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_RULE.ERROR_MESSAGE') }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <div class="grid-2">
                        <label>
                            <input v-model.trim="withdrawalFeeIsPercentage" type="checkbox" />
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.WITHDRAWAL_FEE_IS_PERCENTAGE.LABEL') }}
                        </label>
                    </div>

                    <div class="grid-2">
                        <label v-if="withdrawalFeeIsPercentage" :class="{ error: $v.withdrawalFeePercentage.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.WITHDRAWAL_FEE_PERCENTAGE.LABEL') }}
                            <input v-model.trim="withdrawalFeePercentage" type="number"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.WITHDRAWAL_FEE_PERCENTAGE.PLACEHOLDER')"
                                @input="$v.withdrawalFeePercentage.$touch" step="0.01" min="0" />
                            <span v-if="$v.withdrawalFeePercentage.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.WITHDRAWAL_FEE_PERCENTAGE.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label v-else :class="{ error: $v.withdrawalFeeValue.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.WITHDRAWAL_FEE_VALUE.LABEL') }}
                            <input v-model.trim="withdrawalFeeValue" type="text"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.WITHDRAWAL_FEE_VALUE.PLACEHOLDER')"
                                @input="$v.withdrawalFeeValue.$touch"  v-mask="moneyMask"/>
                            <span v-if="$v.withdrawalFeeValue.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.WITHDRAWAL_FEE_VALUE.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>
                </div>
            </fieldset>

            <fieldset v-if="extensionSessionEnable" class="form-section" role="group" aria-labelledby="section-extension">
                <legend id="section-extension" class="section-title cursor-pointer" @click.prevent="toggleSection('extension')">
                    <fluent-icon :icon="collapsed.extension ? 'chevron-right' : 'chevron-down'" size="16" />
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_OPTION_ENABLE.LABEL') || 'Sessão estendida' }}
                </legend>
                <div v-show="!collapsed.extension">
                    <div class="grid-2">
                        <label :class="{ error: $v.extensionSessionAnnuity.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ANNUITY.LABEL') }}
                            <input v-model.trim="extensionSessionAnnuity" type="text" :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ANNUITY.PLACEHOLDER')"
                                @input="$v.extensionSessionAnnuity.$touch" v-mask="moneyMask" />
                            <span v-if="$v.extensionSessionAnnuity.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ANNUITY.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label :class="{ error: $v.extensionSessionInstallmentValue.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_INSTALLMENT_VALUE.LABEL') }}
                            <input v-model.trim="extensionSessionInstallmentValue" type="text"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_INSTALLMENT_VALUE.PLACEHOLDER')"
                                @input="$v.extensionSessionInstallmentValue.$touch" v-mask="moneyMask" disabled />
                            <span v-if="$v.extensionSessionInstallmentValue.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_INSTALLMENT_VALUE.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <div class="row" :class="{ error: $v.extensionSessionShift.$error }">
                            <label>
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.LABEL') }}
                            </label>
                            <multiselect v-model="extensionSessionShift"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.PLACEHOLDER')" selected-label
                                :select-label="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.SELECT_PLACEHOLDER')"
                                :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.REMOVE')" :max-height="160"
                                :options="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.OPTIONS')" :allow-empty="true" :option-height="104"
                                @input="$v.extensionSessionShift.$touch" />
                            <span v-if="$v.extensionSessionShift.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.ERROR_MESSAGE') }}
                            </span>
                        </div>

                        <label :class="{ error: $v.extensionSessionChangeDeadline.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_CHANGE_DEADLINE.LABEL') }}
                            <input v-model.trim="extensionSessionChangeDeadline" type="date"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_CHANGE_DEADLINE.PLACEHOLDER')"
                                @input="$v.extensionSessionChangeDeadline.$touch" />
                            <span v-if="$v.extensionSessionChangeDeadline.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_CHANGE_DEADLINE.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.extensionSessionStart.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_START.LABEL') }}
                            <input v-model.trim="extensionSessionStart" type="number"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_START.PLACEHOLDER')"
                                @input="$v.extensionSessionStart.$touch" step="1" min="0" max="23" />
                            <span v-if="$v.extensionSessionStart.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_START.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label :class="{ error: $v.extensionSessionEnd.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_END.LABEL') }}
                            <input v-model.trim="extensionSessionEnd" type="number"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_END.PLACEHOLDER')"
                                @input="$v.extensionSessionEnd.$touch" step="1" min="0" max="23" />
                            <span v-if="$v.extensionSessionEnd.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_END.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <div class="row" :class="{ error: $v.extensionSessionMeals.$error }">
                            <label>
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MEALS.LABEL') }}
                            </label>
                            <multiselect v-model="extensionSessionMeals"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MEALS.PLACEHOLDER')" selected-label
                                :select-label="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MEALS.SELECT_PLACEHOLDER')" :multiple="true" 
                                :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MEALS.REMOVE')" :max-height="160"
                                :options="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MEALS.OPTIONS')" :allow-empty="true" :option-height="104"/>
                            <span v-if="$v.extensionSessionMeals.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MEALS.ERROR_MESSAGE') }}
                            </span>
                        </div>

                        <div class="row" :class="{ error: $v.extensionSessionWeekdays.$error }">
                            <label>
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_WEEKDAYS.LABEL') }}
                            </label>
                            <multiselect v-model="extensionSessionWeekdays"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_WEEKDAYS.PLACEHOLDER')" selected-label
                                :select-label="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_WEEKDAYS.SELECT_PLACEHOLDER')" :multiple="true" 
                                :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_WEEKDAYS.REMOVE')" :max-height="160"
                                :options="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_WEEKDAYS.OPTIONS')" :allow-empty="true" :option-height="104"/>
                            <span v-if="$v.extensionSessionWeekdays.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_WEEKDAYS.ERROR_MESSAGE') }}
                            </span>
                        </div>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.extensionSessionMinimumMealDays.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MINIMUM_MEAL_DAYS.LABEL') }}
                            <input v-model.trim="extensionSessionMinimumMealDays" type="number"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MINIMUM_MEAL_DAYS.PLACEHOLDER')"
                                @input="$v.extensionSessionMinimumMealDays.$touch" step="1" min="0" />
                            <span v-if="$v.extensionSessionMinimumMealDays.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MINIMUM_MEAL_DAYS.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label :class="{ error: $v.extensionSessionMinAbsenceDaysForMealDiscount.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MIN_ABSENCE_DAYS_FOR_MEAL_DISCOUNT.LABEL') }}
                            <input v-model.trim="extensionSessionMinAbsenceDaysForMealDiscount" type="number"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MIN_ABSENCE_DAYS_FOR_MEAL_DISCOUNT.PLACEHOLDER')"
                                @input="$v.extensionSessionMinAbsenceDaysForMealDiscount.$touch" step="1" min="0" />
                            <span v-if="$v.extensionSessionMinAbsenceDaysForMealDiscount.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_MIN_ABSENCE_DAYS_FOR_MEAL_DISCOUNT.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.extensionSessionAbsenceCutoffDayForMealDiscount.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ABSENCE_CUTOFF_DAY_FOR_MEAL_DISCOUNT.LABEL') }}
                            <input v-model.trim="extensionSessionAbsenceCutoffDayForMealDiscount" type="number"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ABSENCE_CUTOFF_DAY_FOR_MEAL_DISCOUNT.PLACEHOLDER')"
                                @input="$v.extensionSessionAbsenceCutoffDayForMealDiscount.$touch" step="1" min="0" />
                            <span v-if="$v.extensionSessionAbsenceCutoffDayForMealDiscount.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ABSENCE_CUTOFF_DAY_FOR_MEAL_DISCOUNT.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>
                </div>
            </fieldset>

            <fieldset class="form-section" role="group" aria-labelledby="section-responsible">
                <legend id="section-responsible" class="section-title cursor-pointer" @click.prevent="toggleSection('responsible')">
                    <fluent-icon :icon="collapsed.responsible ? 'chevron-right' : 'chevron-down'" size="16" />
                    Dados do responsável financeiro
                </legend>
                <div v-show="!collapsed.responsible">
            <label :class="{ error: $v.contractorName.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_NAME.LABEL') }}
                <input v-model.trim="contractorName" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_NAME.PLACEHOLDER')"
                    @input="$v.contractorName.$touch" />
                <span v-if="$v.contractorName.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_NAME.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.contractorZipCode.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_ZIP_CODE.LABEL') }}
                <input v-model="contractorZipCode" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_ZIP_CODE.PLACEHOLDER')"
                    @input="$v.contractorZipCode.$touch" v-mask="'#####-###'" />
                <span v-if="$v.contractorZipCode.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_ZIP_CODE.ERROR_MESSAGE') }}
                </span>
                <div class="medium-12 field-button">
                    <woot-button variant="clear" @click.prevent="searchContractorZipCode" size="small" disabled>
                        {{ this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_ZIPCODE.BUTTON_LABEL') }}
                    </woot-button>
                </div>
            </label>

            <label :class="{ error: $v.contractorAddress.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_ADDRESS.LABEL') }}
                <input v-model="contractorAddress" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_ADDRESS.PLACEHOLDER')"
                    @input="$v.contractorAddress.$touch" />
                <span v-if="$v.contractorAddress.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_ADDRESS.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.contractorDistrict.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_DISTRICT.LABEL') }}
                <input v-model="contractorDistrict" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_DISTRICT.PLACEHOLDER')"
                    @input="$v.contractorDistrict.$touch" />
                <span v-if="$v.contractorDistrict.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_DISTRICT.ERROR_MESSAGE') }}
                </span>
            </label>

            <div class="row" :class="{ error: $v.contractorState.$error }">
                <label >
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_STATE.LABEL') }}
                </label>
                <multiselect v-model="contractorState" track-by="ID" label="NAME" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_STATE.PLACEHOLDER')" selected-label
                    :select-label="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_STATE.SELECT_PLACEHOLDER')"
                    :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_STATE.REMOVE')" :max-height="160"
                    :options="$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS')" :allow-empty="true" :option-height="104"
                    @input="$v.contractorState.$touch" />
                <span v-if="$v.contractorState.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_STATE.ERROR_MESSAGE') }}
                </span>
            </div>

            <label :class="{ error: $v.contractorCity.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_CITY.LABEL') }}
                <input v-model="contractorCity" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_CITY.PLACEHOLDER')"
                    @input="$v.contractorCity.$touch" />
                <span v-if="$v.contractorCity.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_CITY.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.contractorPhoneNumber.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_PHONE_NUMBER.LABEL') }}
                <input v-model="contractorPhoneNumber" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_PHONE_NUMBER.PLACEHOLDER')"
                    @input="$v.contractorPhoneNumber.$touch" v-mask="'(##) #########'" />
                <span v-if="$v.contractorPhoneNumber.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_PHONE_NUMBER.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.contractorEmail.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_EMAIL.LABEL') }}
                <input v-model="contractorEmail" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_EMAIL.PLACEHOLDER')"
                    @input="$v.contractorEmail.$touch" />
                <span v-if="$v.contractorEmail.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_EMAIL.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.contractorFirstDocument.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_FIRST_DOCUMENT.LABEL') }}
                <input v-model="contractorFirstDocument" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_FIRST_DOCUMENT.PLACEHOLDER')"
                    @input="$v.contractorFirstDocument.$touch" v-mask="'###.###.###-##'" />

                <span v-if="$v.contractorFirstDocument.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_FIRST_DOCUMENT.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.contractorSecondDocument.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_SECOND_DOCUMENT.LABEL') }}
                <input v-model="contractorSecondDocument" type="text"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_SECOND_DOCUMENT.PLACEHOLDER')"
                    @input="$v.contractorSecondDocument.$touch" />
                <span v-if="$v.contractorSecondDocument.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_SECOND_DOCUMENT.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.contractorBirthDate.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_BIRTH_DATE.LABEL') }}
                <input v-model.trim="contractorBirthDate" type="date" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_BIRTH_DATE.PLACEHOLDER')"
                    @input="$v.contractorBirthDate.$touch" :max="maxBirthDate" />

                <span v-if="$v.contractorBirthDate.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_BIRTH_DATE.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.contractorOccupation.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_OCCUPATION.LABEL') }}
                <input v-model="contractorOccupation" type="text"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_OCCUPATION.PLACEHOLDER')"
                    @input="$v.contractorOccupation.$touch" />

                <span v-if="$v.contractorOccupation.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_OCCUPATION.ERROR_MESSAGE') }}
                </span>
            </label>

            <div class="row" :class="{ error: $v.contractorMaritalStatus.$error }">
                <label>
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_MARITAL_STATUS.LABEL') }}
                </label>
                <multiselect v-model="contractorMaritalStatus"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_MARITAL_STATUS.PLACEHOLDER')" selected-label
                    :select-label="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_MARITAL_STATUS.SELECT_PLACEHOLDER')"
                    :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_MARITAL_STATUS.REMOVE')" :max-height="160"
                    :options="this.$t('SCHOOL_CONTACT_FORM.FORM.MARITAL_STATUS.STATUS')" :allow-empty="true"
                    :option-height="104" @input="$v.contractorMaritalStatus.$touch" />

                <span v-if="$v.contractorMaritalStatus.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_MARITAL_STATUS.ERROR_MESSAGE') }}
                </span>
            </div>

            <div class="row" :class="{ error: $v.relationshipWithStudent.$error }">
                <label>
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RELATIONSHIP_WITH_STUDENT.LABEL') }}
                </label>
                <multiselect v-model="relationshipWithStudent" track-by="ID" label="NAME"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RELATIONSHIP_WITH_STUDENT.PLACEHOLDER')" selected-label
                    :select-label="$t('SCHOOL_CONTACT_FORM.FORM.RELATIONSHIP_WITH_STUDENT.SELECT_PLACEHOLDER')"
                    :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.RELATIONSHIP_WITH_STUDENT.REMOVE')" :max-height="160"
                    :options="$t('SCHOOL_CONTACT_FORM.FORM.RELATIONSHIP_WITH_STUDENT.OPTIONS')" :allow-empty="true"
                    :option-height="104" @input="$v.relationshipWithStudent.$touch" />

                <span v-if="$v.relationshipWithStudent.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RELATIONSHIP_WITH_STUDENT.ERROR_MESSAGE') }}
                </span>
            </div>

            <div class="row" :class="{ error: $v.studentParentsMaritalStatus.$error }">
                <label>
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.LABEL') }}
                </label>
                <multiselect v-model="studentParentsMaritalStatus" track-by="ID" label="NAME"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.PLACEHOLDER')" selected-label
                    :select-label="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.SELECT_PLACEHOLDER')"
                    :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.REMOVE')" :max-height="160"
                    :options="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.OPTIONS')" :allow-empty="true"
                    :option-height="104" @input="$v.studentParentsMaritalStatus.$touch" />
                
                <span v-if="$v.studentParentsMaritalStatus.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.ERROR_MESSAGE') }}
                </span>
            </div>

            <label v-if="studentParentsMaritalStatus.ID === 'OTHER'" :class="{ error: $v.studentParentsMaritalStatusOther.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.OTHER.LABEL') }}
                <input v-model="studentParentsMaritalStatusOther" type="text"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.OTHER.PLACEHOLDER')"
                    @input="$v.studentParentsMaritalStatusOther.$touch" />

                <span v-if="$v.studentParentsMaritalStatusOther.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.OTHER.ERROR_MESSAGE') }}
                </span>
            </label>

            <div class="row" v-if="studentParentsMaritalStatus.ID === 'DIVORCED'" :class="{ error: $v.studentCustodyArrangement.$error }">
                <label>
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_CUSTODY_ARRANGEMENT.LABEL') }}
                </label>
                <multiselect v-model="studentCustodyArrangement" track-by="ID" label="NAME"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_CUSTODY_ARRANGEMENT.PLACEHOLDER')" selected-label
                    :select-label="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_CUSTODY_ARRANGEMENT.SELECT_PLACEHOLDER')"
                    :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_CUSTODY_ARRANGEMENT.REMOVE')" :max-height="160"
                    :options="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_CUSTODY_ARRANGEMENT.OPTIONS')" :allow-empty="true"
                    :option-height="104" @input="$v.studentCustodyArrangement.$touch" />

                <span v-if="$v.studentCustodyArrangement.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_CUSTODY_ARRANGEMENT.ERROR_MESSAGE') }}
                </span>
            </div>
                </div>
            </fieldset>

            <fieldset class="form-section" role="group" aria-labelledby="section-student">
                <legend id="section-student" class="section-title cursor-pointer" @click.prevent="toggleSection('student')">
                    <fluent-icon :icon="collapsed.student ? 'chevron-right' : 'chevron-down'" size="16" />
                    Dados do aluno
                </legend>
                <div v-show="!collapsed.student">
                    <div class="grid-2">
                        <label :class="{ error: $v.studentName.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_STUDENT_NAME.LABEL') }}
                            <input v-model="studentName" type="text" :disabled="true"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_STUDENT_NAME.PLACEHOLDER')"
                                @input="$v.studentName.$touch" />
                            <span v-if="$v.studentName.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_STUDENT_NAME.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label :class="{ error: $v.studentFirstDocument.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_STUDENT_FIRST_DOCUMENT.LABEL') }}
                            <input v-model="studentFirstDocument" type="text" :disabled="true"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_STUDENT_FIRST_DOCUMENT.PLACEHOLDER')"
                                @input="$v.studentFirstDocument.$touch" v-mask="'###.###.###-##'" />
                            <span v-if="$v.studentFirstDocument.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_STUDENT_FIRST_DOCUMENT.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.studentDateOfBirth.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_DATE_OF_BIRTH.LABEL') }}
                            <input v-model.trim="studentDateOfBirth" type="date" :disabled="true"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_DATE_OF_BIRTH.PLACEHOLDER')"
                                @input="$v.studentDateOfBirth.$touch" />
                            <span v-if="$v.studentDateOfBirth.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_DATE_OF_BIRTH.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <div class="row" :class="{ error: $v.studentSchoolingType.$error }">
                            <label>
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.LABEL') }}
                            </label>
                            <multiselect v-model="studentSchoolingType" track-by="VALUE" label="LABEL" :disabled="true"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.PLACEHOLDER')" selected-label
                                :select-label="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.SELECT_PLACEHOLDER')"
                                :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.REMOVE')" :max-height="160"
                                :options="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.OPTIONS')" :allow-empty="true"
                                :option-height="104" @input="$v.studentSchoolingType.$touch" />
                            <span v-if="$v.studentSchoolingType.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.ERROR_MESSAGE') }}
                            </span>
                        </div>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.studentSchoolClass.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOL_CLASS.LABEL') }}
                            <input v-model="studentSchoolClass" type="text" :disabled="true"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOL_CLASS.PLACEHOLDER')"
                                @input="$v.studentSchoolClass.$touch" />
                            <span v-if="$v.studentSchoolClass.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOL_CLASS.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>
                </div>
            </fieldset>

            <fieldset class="form-section" role="group" aria-labelledby="section-school">
                <legend id="section-school" class="section-title cursor-pointer" @click.prevent="toggleSection('school')">
                    <fluent-icon :icon="collapsed.school ? 'chevron-right' : 'chevron-down'" size="16" />
                    Dados da escola
                </legend>
                <div v-show="!collapsed.school">
                    <div class="grid-2">
                        <label :class="{ error: $v.schoolName.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_NAME.LABEL') }}
                            <input v-model.trim="schoolName" type="text" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_NAME.PLACEHOLDER')" @input="$v.schoolName.$touch" />
                            <span v-if="$v.schoolName.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_NAME.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label :class="{ error: $v.schoolCorporateName.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CORPORATE_NAME.LABEL') }}
                            <input v-model.trim="schoolCorporateName" type="text" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CORPORATE_NAME.PLACEHOLDER')" @input="$v.schoolCorporateName.$touch" />
                            <span v-if="$v.schoolCorporateName.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CORPORATE_NAME.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.schoolDocument.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_DOCUMENT.LABEL') }}
                            <input v-model.trim="schoolDocument" type="text" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_DOCUMENT.PLACEHOLDER')"
                                @input="$v.schoolDocument.$touch" v-mask="'##.###.###/####-##'" />
                            <span v-if="$v.schoolDocument.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_DOCUMENT.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label :class="{ error: $v.schoolZipCode.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ZIP_CODE.LABEL') }}
                            <input v-model.trim="schoolZipCode" type="text" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ZIP_CODE.PLACEHOLDER')"
                                @input="$v.schoolZipCode.$touch" v-mask="'#####-###'" />
                            <span v-if="$v.schoolZipCode.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ZIP_CODE.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <div class="row" :class="{ error: $v.schoolState.$error }">
                            <label>
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_STATE.LABEL') }}
                            </label>
                            <multiselect v-model="schoolState" track-by='ID' label="NAME" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_STATE.PLACEHOLDER')" selected-label
                                :select-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_STATE.SELECT_PLACEHOLDER')"
                                :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_STATE.REMOVE')" :max-height="160"
                                :options="$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS')" :allow-empty="true" :option-height="104" />
                            <span v-if="$v.schoolState.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_STATE.ERROR_MESSAGE') }}
                            </span>
                        </div>

                        <label :class="{ error: $v.schoolCity.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CITY.LABEL') }}
                            <input v-model.trim="schoolCity" type="text" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CITY.PLACEHOLDER')"
                                @input="$v.schoolCity.$touch" />
                            <span v-if="$v.schoolCity.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CITY.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.schoolDistrict.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_DISTRICT.LABEL') }}
                            <input v-model.trim="schoolDistrict" type="text" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_DISTRICT.PLACEHOLDER')"
                                @input="$v.schoolDistrict.$touch" />
                            <span v-if="$v.schoolDistrict.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_DISTRICT.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label :class="{ error: $v.schoolAddress.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ADDRESS.LABEL') }}
                            <input v-model.trim="schoolAddress" type="text" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ADDRESS.PLACEHOLDER')"
                                @input="$v.schoolAddress.$touch" />
                            <span v-if="$v.schoolAddress.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ADDRESS.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>

                    <div class="grid-2">
                        <label :class="{ error: $v.schoolPhoneNumber.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_PHONE_NUMBER.LABEL') }}
                            <input v-model.trim="schoolPhoneNumber" type="text" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_PHONE_NUMBER.PLACEHOLDER')"
                                @input="$v.schoolPhoneNumber.$touch" v-mask="'(##) #########'" />
                            <span v-if="$v.schoolPhoneNumber.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_PHONE_NUMBER.ERROR_MESSAGE') }}
                            </span>
                        </label>

                        <label :class="{ error: $v.schoolEmail.$error }">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_EMAIL.LABEL') }}
                            <input v-model.trim="schoolEmail" type="text" disabled
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_EMAIL.PLACEHOLDER')" @input="$v.schoolEmail.$touch" />
                            <span v-if="$v.schoolEmail.$error" class="message">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_EMAIL.ERROR_MESSAGE') }}
                            </span>
                        </label>
                    </div>
                </div>
            </fieldset>

            

            

            

            

            

            

            

            

            

            

            

            

            

            

            

            

            

            <label v-if="extensionSessionEnable" :class="{ error: $v.extensionSessionAnnuity.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ANNUITY.LABEL') }}
                <input v-model.trim="extensionSessionAnnuity" type="text" :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ANNUITY.PLACEHOLDER')"
                    @input="$v.extensionSessionAnnuity.$touch" v-mask="moneyMask" />
                <span v-if="$v.extensionSessionAnnuity.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ANNUITY.ERROR_MESSAGE') }}
                </span>
            </label>

            <label v-if="extensionSessionEnable" :class="{ error: $v.extensionSessionInstallmentValue.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_INSTALLMENT_VALUE.LABEL') }}
                <input v-model.trim="extensionSessionInstallmentValue" type="text"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_INSTALLMENT_VALUE.PLACEHOLDER')"
                    @input="$v.extensionSessionInstallmentValue.$touch" v-mask="moneyMask" disabled />
                <span v-if="$v.extensionSessionInstallmentValue.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_INSTALLMENT_VALUE.ERROR_MESSAGE') }}
                </span>
            </label>

            <div v-if="extensionSessionEnable" class="row" :class="{ error: $v.extensionSessionShift.$error }">
                <label >
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.LABEL') }}
                </label>
                <multiselect v-model="extensionSessionShift"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.PLACEHOLDER')" selected-label
                    :select-label="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.SELECT_PLACEHOLDER')"
                    :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.REMOVE')" :max-height="160"
                    :options="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.OPTIONS')" :allow-empty="true" :option-height="104"
                    @input="$v.extensionSessionShift.$touch" />
                <span v-if="$v.extensionSessionShift.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_SHIFT.ERROR_MESSAGE') }}
                </span>
            </div>

            <label v-if="extensionSessionEnable" :class="{ error: $v.extensionSessionStart.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_START.LABEL') }}
                <input v-model.trim="extensionSessionStart" type="number"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_START.PLACEHOLDER')"
                    @input="$v.extensionSessionStart.$touch" step="1" min="0" max="23" />
                <span v-if="$v.extensionSessionStart.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_START.ERROR_MESSAGE') }}
                </span>
            </label>

            <label v-if="extensionSessionEnable" :class="{ error: $v.extensionSessionEnd.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_END.LABEL') }}
                <input v-model.trim="extensionSessionEnd" type="number"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_END.PLACEHOLDER')"
                    @input="$v.extensionSessionEnd.$touch" step="1" min="0" max="23" />
                <span v-if="$v.extensionSessionEnd.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_END.ERROR_MESSAGE') }}
                </span>
            </label>

            

            

            

            

            <!-- <section>
                <label>
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.LABEL') }}
                </label>
                <div class="medium-12 columns filters-wrap">
                    <div class="filters">
                        <div>
                            <div v-if="witnesses.length === 0">
                                <label class="filter-none">
                                    {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.NO_HAVE_WITNESS') }}
                                </label>
                            </div>
                            <div v-else>
                                <div class="filter" v-for="(field, index) in witnesses" :key="index">
                                    <label class="filter-title">
                                        {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.HEADER_TITLE') }} {{ index + 1 }}:
                                    </label>
                                    <label :class="{ error: $v.witnesses.$each[index].name.$error }">
                                        {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.NAME_LABEL') }}
                                        <input v-model.trim="field.name" type="text"
                                            :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.WITNESS.NAME_PLACEHOLDER')"
                                            @input="$v.witnesses.$each[index].name.$touch" />
                                        <span v-if="$v.witnesses.$each[index].name.$error" class="message">
                                            {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.ERROR_NAME_MESSAGE') }}
                                        </span>
                                    </label>
                                    <label :class="{ error: $v.witnesses.$each[index].phone.$error }">
                                        {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.PHONE_LABEL') }}
                                        <input v-model.trim="field.phone" type="text"
                                            :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.WITNESS.PHONE_PLACEHOLDER')"
                                            @input="$v.witnesses.$each[index].phone.$touch" v-mask="'(##) #########'" />
                                        <span v-if="$v.witnesses.$each[index].phone.$error" class="message">
                                            {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.ERROR_PHONE_MESSAGE') }}
                                        </span>
                                    </label>
                                    <label :class="{ error: $v.witnesses.$each[index].email.$error }">
                                        {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.EMAIL_LABEL') }}
                                        <input v-model.trim="field.email" type="text"
                                            :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.WITNESS.EMAIL_PLACEHOLDER')"
                                            @input="$v.witnesses.$each[index].email.$touch" />
                                        <span v-if="$v.witnesses.$each[index].email.$error" class="message">
                                            {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.ERROR_EMAIL_MESSAGE') }}
                                        </span>
                                    </label>
                                    <div class="filter-delete">
                                      <woot-button
                                        icon="delete"
                                        color-scheme="alert"
                                        variant="smooth"
                                        size="small"
                                        @click.prevent="removeWitness(index)">
                                            {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.REMOVE_BUTTON') }}
                                      </woot-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="filter-add">
                        <woot-button
                            icon="add"
                            color-scheme="success"
                            variant="smooth"
                            size="small"
                            @click.prevent="addWitness()">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.WITNESS.ADD_BUTTON') }}
                        </woot-button>
                    </div>
                </div>
            </section> -->

            <div v-if="editingBlockedMessage" class="alert-message warning margin-bottom-1">
                <fluent-icon icon="warning" size="16" class="text-yellow-600" />
                <span class="text-sm">{{ editingBlockedMessage }}</span>
            </div>

            <div class="modal-footer">
                <div class="medium-12 columns">
                    <woot-button
                        :is-loading="isSavingData"
                        :disabled="isProcessing"
                        @click.prevent="saveData"
                    >
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.SAVE_DATA.BUTTON_TEXT') }}
                    </woot-button>
                    <woot-button
                        :is-loading="isGeneratingContract"
                        :disabled="isProcessing || !contractSaved"
                        color-scheme="primary"
                        @click.prevent="generateContractAndSend"
                    >
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.GENERATE_CONTRACT.BUTTON_TEXT') }}
                    </woot-button>
                    <button class="button clear" @click.prevent="onCancel">
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.CANCEL') }}
                    </button>
                </div>
            </div>
        </div>
        <div>
            <fieldset v-if="enrollment && contractType === 'Manual'" class="form-section" role="group" aria-labelledby="section-manual">
                <legend id="section-manual" class="section-title cursor-pointer" @click.prevent="toggleSection('manual')">
                    <fluent-icon :icon="collapsed.manual ? 'chevron-right' : 'chevron-down'" size="16" />
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_FILE.LABEL') }}
                </legend>
                <div v-show="!collapsed.manual">
                    <div class="medium-12 row file-section-wrap">
                        <label v-if="!contractFiles || contractFiles.length === 0" class="medium-12 file-section-empty-label">
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_FILE.EMPTY_LABEL') }}
                        </label>
                        <div v-for="(contractFile, index) in contractFiles" class="medium-12 row file-section-item" >
                            <div v-if="contractFile && contractFile.data_url" class="medium-12 flex items-center justify-center">
                                <attachment-box 
                                    :url="contractFile.data_url"
                                />
                            </div>
                            <div class="medium-12 items-center flex justify-center">
                                <woot-button
                                    v-if="contractFile && contractFile.data_url"
                                    icon="delete"
                                    color-scheme="alert"
                                    variant="smooth"
                                    size="small"
                                    :disabled="isEditingBlocked"
                                    @click.prevent="removeFile(contractFile.id)"
                                >
                                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_FILE.REMOVE_BUTTON_LABEL') }}
                                </woot-button>
                            </div>
                        </div>
                        <input type="file"
                            ref="fileInput" 
                            accept=".jpg, .jpeg, .png, .gif, .pdf, .doc, .docx"
                            @input="handleAttachFile"
                            style="display: none;"
                        />
                        <woot-button
                            v-if = "!contractFiles || contractFiles.length < 5"
                            class = "file-section-upload-button"
                            icon="upload"
                            color-scheme="primary"
                            variant="smooth"
                            size="small"
                            :disabled="isEditingBlocked"
                            @click.prevent="openFileInput()"
                        >
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACT_FILE.ADD_BUTTON_LABEL') }}
                        </woot-button>
                    </div>
                </div>
            </fieldset>
        </div>
    </form>
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { required, minLength, email } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import maskMixin from 'shared/mixins/inputMaskMixin';
import zipCodeMixin from 'shared/mixins/zipCodeMixin';
import { isKeduFieldValid } from 'shared/helpers/KeduValidators';
import AttachmentBox from 'dashboard/components/widgets/conversation/bubble/File.vue';

export default {
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        inProgress: {
            type: Boolean,
            default: false,
        },
        selectedSchoolLabels: {
            type: Array,
            default: () => [],
        },
        contractEditStatus: {
            type: Object,
            default: null,
        },
    },
    components: {
        AttachmentBox
    },
    data() {
        return {
            enrollment: null,
            contractFiles: [],
            installmentsQuantity: null,
            isSavingData: false,
            isGeneratingContract: false,
            contractSaved: false,
            isPopulatingData: false,

            contractorName: '',
            contractorAddress: '',
            contractorDistrict: '',
            contractorState: '',
            contractorCity: '',
            contractorZipCode: '',
            contractorPhoneNumber: '',
            contractorEmail: '',
            contractorFirstDocument: '',
            contractorSecondDocument: '',
            contractorBirthDate: '',
            contractorOccupation: '',
            contractorMaritalStatus: '',

            relationshipWithStudent: '',
            studentParentsMaritalStatus: '',
            studentParentsMaritalStatusOther: '',
            studentCustodyArrangement: '',

            studentName: '',
            studentFirstDocument: '',
            studentDateOfBirth: '',
            studentSchoolingType: '',
            studentSchoolClass: '',

            schoolCity: '',
            schoolEmail: '',
            schoolDocument: '',
            schoolName: '',
            schoolCorporateName: '',
            schoolDistrict: '',
            schoolPhoneNumber: '',
            schoolState: '',
            schoolAddress: '',
            schoolZipCode: '',

            installmentMaturityOptions: [5,10,15,20],
            installmentsQuantityOptions: [1,10,11,12,13],

            annuity: '',

            installmentsNumber: '',
            
            installmentsQuantity: '',
            installmentValue: '',
            startDateInstallment: '',
            lastDayInstallment: '',

            punctualityDiscountIsPercentage: true,
            punctualityDiscountPercentage: '',
            punctualityDiscountValue: '',
            contractPunctualityDiscountRule: '',
            withdrawalFeeIsPercentage: true,
            withdrawalFeePercentage: '',
            withdrawalFeeValue: '',
            installmentsMaturity: '',
            remainingInstallmentsMaturity: '',

            extensionSessionAbsenceCutoffDayForMealDiscount: '',
            extensionSessionAnnuity: '',
            extensionSessionChangeDeadline: '',
            extensionSessionStart: '',
            extensionSessionEnd: '',
            extensionSessionInstallmentValue: '',
            extensionSessionMeals: '',
            extensionSessionMinAbsenceDaysForMealDiscount: '',
            extensionSessionMinimumMealDays: '',
            extensionSessionShift: '',
            extensionSessionWeekdays: this.$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_WEEKDAYS.OPTIONS'),

            witnesses: [],

            maxBirthDate: this.getMaxBirthDate(),
            contractType: null,
            contractTypes: [],
            extensionSessionEnable: false,
            punctualityDiscountRuleEqualToDueDate: false,
            showLoadingModal: false,
            collapsed: {
                enrollmentSelect: false,
                financial: false,
                responsible: false,
                address: false,
                student: false,
                school: true,
                extension: false,
                manual: false,
            },
            currentSchoolData: null,
        };
    },
    mixins: [alertMixin, maskMixin, zipCodeMixin],
    validations() {
           let validations = {
                contractorName: { required, keduValidator: this.checkDataValidation('fullname') },
                contractorAddress: { required },
                contractorDistrict: { required },
                contractorState: { required },
                contractorCity: { required, keduValidator: this.checkDataValidation('city') },
                contractorZipCode: { required, keduValidator: this.checkDataValidation('zipCode') },
                contractorPhoneNumber: { required, minLength: minLength(8), },
                contractorEmail: { required, email, },
                contractorFirstDocument: { required, keduValidator: this.checkDataValidation('firstDocument') },
                contractorSecondDocument: { minLength: minLength(4) },
                contractorBirthDate: { required },
                contractorOccupation: { required },
                contractorMaritalStatus: { required },

                relationshipWithStudent: { required },
                studentParentsMaritalStatus: { required },

                studentName: { required, keduValidator: this.checkDataValidation('fullname') },
                studentFirstDocument: { },
                studentDateOfBirth: { required },
                studentSchoolingType: { required },
                studentSchoolClass: { required },

                schoolName: { required, minLength: minLength(4), },
                schoolCorporateName: { required },
                schoolDocument: { required },
                schoolZipCode: { required, keduValidator: this.checkDataValidation('zipCode') },
                schoolState: { required },
                schoolCity: { required, keduValidator: this.checkDataValidation('city') },
                schoolDistrict: { required },
                schoolAddress: { required },
                schoolPhoneNumber: { required },
                schoolEmail: { required, email, },

                annuity: { required, moneyGreaterThanZero: this.validateMoneyGreaterThanZero },

                installmentsQuantity: { required },
                installmentValue: { required, moneyGreaterThanZero: this.validateMoneyGreaterThanZero },
                startDateInstallment: { required, notRetroactive: () => !(this.startDateInstallment && this.todayDate && this.startDateInstallment < this.todayDate.substring(0, 7)) },
                lastDayInstallment: { required },

                punctualityDiscountPercentage: {},
                punctualityDiscountValue: {},
                withdrawalFeePercentage: {},
                withdrawalFeeValue: {},
                installmentsMaturity: { required },
                remainingInstallmentsMaturity: { required },
                studentParentsMaritalStatusOther: {},
                studentCustodyArrangement: {},

                witnesses: {
                    $each: {
                        name: { required, keduValidator: this.checkDataValidation('fullname') },
                        phone: { minLength: minLength(8) },
                        email: { required, email },
                    },
                },

                extensionSessionAbsenceCutoffDayForMealDiscount: {},
                extensionSessionAnnuity: {},
                extensionSessionChangeDeadline: {},
                extensionSessionStart: {},
                extensionSessionEnd: {},
                extensionSessionInstallmentValue: {},
                extensionSessionMeals: {},
                extensionSessionMinAbsenceDaysForMealDiscount: {},
                extensionSessionMinimumMealDays: {},
                extensionSessionShift: {},
                extensionSessionWeekdays: {},

                contractPunctualityDiscountRule: {}
                
        };

        if(this.punctualityDiscountIsPercentage){
            validations = { ...validations, punctualityDiscountPercentage: { required }, punctualityDiscountValue: {}}
        } else {
            validations = { ...validations, punctualityDiscountPercentage: {}, punctualityDiscountValue: { required }}
        }

    
        if(this.withdrawalFeeIsPercentage){
            validations = { ...validations, withdrawalFeePercentage: { required },  withdrawalFeeValue: {}}
        } else {
            validations = { ...validations, withdrawalFeePercentage: {}, withdrawalFeeValue: { required }}
        }

        
        if (this.studentParentsMaritalStatus?.ID === "OTHER") {
            validations = { ...validations, studentParentsMaritalStatusOther: { required } }
        } else if (this.studentParentsMaritalStatus?.ID === "DIVORCED") {
            validations = { ...validations, studentCustodyArrangement: { required } }
        }

        if (this.extensionSessionEnable) {
            validations = { 
                ...validations,
                extensionSessionAbsenceCutoffDayForMealDiscount: { required },
                extensionSessionAnnuity: { required },
                extensionSessionChangeDeadline: { required },
                extensionSessionStart: { required },
                extensionSessionEnd: { required },
                extensionSessionInstallmentValue: { required },
                extensionSessionMeals: { required },
                extensionSessionMinAbsenceDaysForMealDiscount: { required },
                extensionSessionMinimumMealDays: { required },
                extensionSessionShift: { required },
                extensionSessionWeekdays: { required }
            }
        }

        if(!this.punctualityDiscountRuleEqualToDueDate) {
            validations = { 
                ...validations,
                contractPunctualityDiscountRule: { 
                  required, 
                  punctualityDiscountRuleGreaterThanTuitionDueDate: () => {
                      if (!this.contractPunctualityDiscountRule || !this.firstInstallmentDueDate) return true; 
                      
                      return this.contractPunctualityDiscountRule <= this.firstInstallmentDueDate;
                  }
                }
            }
        }

        return validations
    },
    computed: {
        ...mapGetters({
            enrollments: 'schoolEnrollments/getSchoolEnrollments',
            uiFlags: 'schoolEnrollments/getUIFlags',
            currentEnrollment:'schoolEnrollments/getEnrollment',
            getAccount: 'accounts/getAccount',
            accountId: 'getCurrentAccountId',
        }),
        todayDate() {
            return new Date().toISOString().split('T')[0];
        },
        currentAccount() {
            return this.getAccount(this.accountId);
        },
        checkDataValidation() {
            return (dataParams) => (value) => isKeduFieldValid(value, dataParams);
        },
        firstDueDayOptions() {
            if (!this.startDateInstallment || !this.installmentMaturityOptions) {
                return this.installmentMaturityOptions || [];
            }
            const now = new Date();
            const [currYear, currMonth, currDay] = [now.getFullYear(), now.getMonth() + 1, now.getDate()];
            const [selYear, selMonth] = this.startDateInstallment.split('-').map(Number);
            
            const maxDaysInMonth = new Date(selYear, selMonth, 0).getDate();

            return this.installmentMaturityOptions.filter(dayStr => {
                const day = parseInt(dayStr, 10);
                const isCurrentMonth = selYear === currYear && selMonth === currMonth;
                return day <= maxDaysInMonth && (isCurrentMonth ? day >= currDay : true);
            });
        },
        remainingMonths() {
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth(); // getMonth() returns 0 for January, so we add 1
            const monthsInYear = 12;

            const numberOfRemainingMonths = monthsInYear - currentMonth;

            const remainingMonthsArray = Array.from({ length: numberOfRemainingMonths }, (_, i) => i + 1);
            
            return remainingMonthsArray;
        },
        selectedEnrollmentYear() {
            return this.enrollment?.school_year ? parseInt(this.enrollment.school_year, 10) : null;
        },
        isEnrollmentYearBlocked() {
            return this.isYearLocked(this.enrollment?.school_year);
        },
        startDateMin() {
            if (!this.selectedEnrollmentYear) return null;
            const currentYearMonth = this.todayDate.substring(0, 7);
            const jan = `${this.enrollment.school_year}-01`;
            return jan > currentYearMonth ? jan : currentYearMonth;
        },
        startDateMax() {
            return this.selectedEnrollmentYear ? `${this.enrollment.school_year}-12` : null;
        },
        firstInstallmentDueDate() {
            if (!this.startDateInstallment) return null;
            const day = String(this.installmentsMaturity || '01').padStart(2, '0');
            return `${this.startDateInstallment}-${day}`;
        },
        allowedInstallmentsByStartDate() {
            if (!this.selectedEnrollmentYear || !this.startDateInstallment) return 12;

            const startDate = new Date(this.startDateInstallment);

            if (this.currentSchoolData?.allow_installments_after_selected_year) {
                const startYear = startDate.getUTCFullYear();
                const startMonth = startDate.getUTCMonth();
                const enrollmentYear = parseInt(this.selectedEnrollmentYear, 10);
                const maxYear = enrollmentYear + 1;
                const maxMonth = 0;

                const monthsAvailable = (maxYear - startYear) * 12 + (maxMonth - startMonth) + 1;
                return Math.max(0, Math.min(12, monthsAvailable));
            }

            const month = startDate.getUTCMonth() + 1;
            return 12 - month + 1;
        },
        // Disable editing when there's a pending digital contract
        isPendingContractBlocking() {
            return this.contractEditStatus && !this.contractEditStatus.can_edit;
        },
        editingBlockedMessage() {
            if (this.isPendingContractBlocking && this.contractEditStatus?.message) {
                return this.contractEditStatus.message;
            }

            if (this.isEnrollmentYearBlocked) {
                return this.getEnrollmentYearLockedMessage();
            }

            return null;
        },
        isEditingBlocked() {
            return this.isPendingContractBlocking || this.isEnrollmentYearBlocked;
        },
        isProcessing() {
            return this.isSavingData || this.isGeneratingContract || this.inProgress || this.uiFlags.isCreating || this.uiFlags.isUpdating || this.uiFlags.isFetching || this.isEditingBlocked;
        },
    },
    watch: {
        enrollment(newEnrollment) {
            if (newEnrollment && newEnrollment.id) {
                this.$emit('enrollment-selected', newEnrollment.id);
            }
        },
        punctualityDiscountPercentage(val) {
            if (val > 90) {
                this.punctualityDiscountPercentage = 90;
            }
            this.punctualityDiscountValue = ((this.punctualityDiscountPercentage / 100) * this.moneyMaskToDecimal(this.installmentValue)).toFixed(2);
        },
        punctualityDiscountValue(val) {
            const limitValue = (Math.floor(this.moneyMaskToDecimal(this.installmentValue) * 90) / 100).toFixed(2);
            if (this.moneyMaskToDecimal(val) > limitValue) {
                this.punctualityDiscountValue = limitValue;
            }
        },
        annuity(val) {
            this.installmentValue = this.roundMoneyToFloor(this.moneyMaskToDecimal(val) / this.installmentsQuantity);
        },
        extensionSessionAnnuity(val) {
            this.extensionSessionInstallmentValue = this.roundMoneyToFloor(this.moneyMaskToDecimal(val) / this.installmentsQuantity);
        },
        installmentsQuantity(val) {
            if (!val) return;

            this.installmentValue = this.roundMoneyToFloor(this.moneyMaskToDecimal(this.annuity) / val);
            if (this.startDateInstallment && this.installmentsQuantity) {
                this.updateLastDayInstallment();
                
                if (this.selectedEnrollmentYear && new Date(this.lastDayInstallment).getFullYear() !== this.selectedEnrollmentYear) {
                    const allowed = this.allowedInstallmentsByStartDate;
                    if (this.installmentsQuantity > allowed) {
                         this.installmentsQuantity = Math.max(1, allowed);
                         const ts = new Date(this.startDateInstallment).setMonth(new Date(this.startDateInstallment).getMonth() + (this.installmentsQuantity - 1));
                         this.lastDayInstallment = new Date(ts).toISOString().split('T')[0];
                         this.updateLastDayInstallment();
                    }
                }

                if (this.installmentsQuantity > this.allowedInstallmentsByStartDate) {
                    this.installmentsQuantity = this.allowedInstallmentsByStartDate;
                }
                
                this.updateLastDayInstallment();
            }
        },
        startDateInstallment(val) {
            if (!val || val.length !== 7) return; 
            const inputYear = parseInt(val.split('-')[0], 10);

            if (this.selectedEnrollmentYear && inputYear !== this.selectedEnrollmentYear) {
                this.installmentsQuantity = null;
                this.installmentsMaturity = '';
                this.lastDayInstallment = '';
                this.installmentsQuantityOptions = [];
                return;
            }

            if (this.installmentsMaturity) {
                const [year, month] = val.split('-');
                const maxDays = new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
                if (this.installmentsMaturity > maxDays) {
                    this.installmentsMaturity = maxDays;
                }
            }

            if (this.selectedEnrollmentYear) {
                const date = new Date(`${val}-01T00:00:00`); 
                const year = date.getUTCFullYear();

                if (Number.isNaN(year)) {
                    this.startDateInstallment = '';
                    this.lastDayInstallment = '';
                    return;
                }

                const month = date.getUTCMonth();
                const enrollmentYear = parseInt(this.selectedEnrollmentYear, 10);
                const isExtended = this.currentSchoolData?.allow_installments_after_selected_year;
                const maxStartYear = isExtended ? enrollmentYear + 1 : enrollmentYear;

                const exceedsExtendedMonth = isExtended && year === maxStartYear && month > 0;

                if (year > maxStartYear || year < enrollmentYear || exceedsExtendedMonth) {
                    this.startDateInstallment = '';
                    this.lastDayInstallment = '';
                    return;
                }
            }

            this.refreshInstallmentsQuantityOptions();
            this.updateLastDayInstallment();
            if (!this.isPopulatingData) {
                this.installmentsQuantity = '';
                this.lastDayInstallment = '';
            }
        },
        installmentsMaturity(val) {
            if (this.lastDayInstallment) {
                this.updateLastDayInstallment();
            }
        },
        
        remainingInstallmentsMaturity(val) {
            if (this.lastDayInstallment) {
                this.updateLastDayInstallment();
            }
        },
        lastDayInstallment(val) {
            if (!val || !this.selectedEnrollmentYear) return;

            const lastDayDate = new Date(val);
            const enrollmentYear = parseInt(this.selectedEnrollmentYear, 10);
            const isExtended = this.currentSchoolData?.allow_installments_after_selected_year;
            const maxAllowedYear = isExtended ? enrollmentYear + 1 : enrollmentYear;
            const maxAllowedMonth = isExtended ? 0 : 11;
            const lastDayYear = lastDayDate.getUTCFullYear();
            const lastDayMonth = lastDayDate.getUTCMonth();

            const exceedsLimit = lastDayYear > maxAllowedYear ||
                                (lastDayYear === maxAllowedYear && lastDayMonth > maxAllowedMonth);

            if (exceedsLimit) {
                const maxAllowedDate = new Date(Date.UTC(maxAllowedYear, maxAllowedMonth, 1));
                this.lastDayInstallment = this.fixDateMaturityDay(maxAllowedDate);
            }
        }
    },
    mounted() {
        this.fetchLabels();

        if (this.enrollment && this.enrollment.id) {
            this.$emit('enrollment-selected', this.enrollment.id);
        }
    },
    methods: {
        getYearBadgeClass(year) {
            if (!year) return 'year-badge';
            const yearNumber = parseInt(year, 10);
            const colorIndex = yearNumber % 5;
            return `year-badge year-badge--${colorIndex}`;
        },
        isYearLocked(year) {
            if (!year) {
                return false;
            }

            const currentYear = new Date().getFullYear();
            const enrollmentYear = parseInt(String(year).trim(), 10);

            return enrollmentYear < currentYear;
        },
        getEnrollmentYearLockedMessage() {
            const key = 'SCHOOL_CONTACT_FORM.FORM.ENROLLMENT_YEAR_LOCKED';
            const message = this.$t(key);
            return message === key ? 'Você está consultando um contrato do ano anterior.' : message;
        },
        toggleSection(section) {
            this.$set(this.collapsed, section, !this.collapsed[section]);
        },
        onCancel() {
            this.enrollment = null;
            this.extensionSessionEnable = false;
            this.contractSaved = false;
        },
        getStateById(id){
            return this.$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS').find(option => id === option.ID);
        },
        getMaxBirthDate() {
            const currenteDate = new Date();
            const maxBirthDate = new Date(
                currenteDate.getFullYear() - 18,
                currenteDate.getMonth(),
                currenteDate.getDate()
            );
            return maxBirthDate.toISOString().split('T')[0];
        },
        enrollmentPayload() {
            return ({
                contactId: this.contact.id,
                school_contact_id: this.enrollment.school_contact_id,

                contractor_name: this.contractorName,
                contractor_address: this.contractorAddress,
                contractor_district: this.contractorDistrict,
                contractor_state: this.contractorState.ID,
                contractor_city: this.contractorCity,
                contractor_zip_code: this.contractorZipCode,
                contractor_phone_number: this.contractorPhoneNumber,
                contractor_email: this.contractorEmail,
                contractor_first_document: this.contractorFirstDocument,
                contractor_second_document: this.contractorSecondDocument,
                contractor_birth_date: this.contractorBirthDate,
                contractor_occupation: this.contractorOccupation,
                contractor_marital_status: this.contractorMaritalStatus,

                relationship_with_student: this.relationshipWithStudent?.NAME,
                student_parents_marital_status: this.studentParentsMaritalStatus?.ID === "OTHER" ? this.studentParentsMaritalStatusOther : this.studentParentsMaritalStatus?.NAME,
                student_custody_arrangement: this.studentParentsMaritalStatus?.ID === "DIVORCED" && this.studentCustodyArrangement?.NAME,

                contract_student_name: this.studentName,
                contract_student_first_document: this.studentFirstDocument,
                student_date_of_birth: this.studentDateOfBirth,
                student_school_class: this.studentSchoolClass,
                student_schooling_type: this.studentSchoolingType?.VALUE,

                school_city: this.schoolCity,
                school_email: this.schoolEmail,
                school_document: this.schoolDocument,
                school_name: this.schoolName,
                school_corporate_name: this.schoolCorporateName,
                school_district: this.schoolDistrict,
                school_phone_number: this.schoolPhoneNumber,
                school_state: this.schoolState?.ID,
                school_address: this.schoolAddress,
                school_zip_code: this.schoolZipCode,

                annuity: this.moneyMaskToDecimal(this.annuity),

                installment_quantity: this.installmentsQuantity,
                installment_value: this.moneyMaskToDecimal(this.installmentValue) || null,
                start_date_installment: (this.startDateInstallment && this.installmentsMaturity) 
                    ? `${this.startDateInstallment}-${String(this.installmentsMaturity).padStart(2, '0')}` 
                    : null,

                last_day_installment: this.lastDayInstallment || null,

                punctuality_discount_percentage: this.punctualityDiscountIsPercentage ? this.punctualityDiscountPercentage : null, 
                punctuality_discount_value: !this.punctualityDiscountIsPercentage ? this.moneyMaskToDecimal(this.punctualityDiscountValue || 0) : null,

                withdrawal_fee_percentage: this.withdrawalFeeIsPercentage ? this.withdrawalFeePercentage : null,
                withdrawal_fee_value: !this.withdrawalFeeIsPercentage ? this.moneyMaskToDecimal(this.withdrawalFeeValue || 0) : null,

                installments_maturity: this.installmentsMaturity,
                remaining_installments_maturity: this.remainingInstallmentsMaturity,
                witnesses: JSON.stringify(this.witnesses),

                extension_session_absence_cutoff_day_for_meal_discount: this.extensionSessionEnable ? this.extensionSessionAbsenceCutoffDayForMealDiscount : null,
                extension_session_annuity: this.extensionSessionEnable ?  this.moneyMaskToDecimal(this.extensionSessionAnnuity) : null,
                extension_session_change_deadline: this.extensionSessionEnable ? this.extensionSessionChangeDeadline : null,
                extension_session_end: this.extensionSessionEnable ? this.extensionSessionEnd : null,
                extension_session_installment_value: this.extensionSessionEnable ? this.moneyMaskToDecimal(this.extensionSessionInstallmentValue) : null,
                extension_session_meals: this.extensionSessionEnable ? this.extensionSessionMeals : null,
                extension_session_min_absence_days_for_meal_discount: this.extensionSessionEnable ? this.extensionSessionMinAbsenceDaysForMealDiscount : null,
                extension_session_minimum_meal_days: this.extensionSessionEnable ? this.extensionSessionMinimumMealDays : null,
                extension_session_shift: this.extensionSessionEnable ? this.extensionSessionShift : null,
                extension_session_start: this.extensionSessionEnable ? this.extensionSessionStart : null,
                extension_session_weekdays: this.extensionSessionEnable ? this.extensionSessionWeekdays : null,
                contract_punctuality_discount_rule: this.punctualityDiscountRuleEqualToDueDate
                    ? `${this.startDateInstallment}-${String(this.installmentsMaturity).padStart(2, '0')}` 
                    : this.contractPunctualityDiscountRule
            });
        },
        hasDataFinalizationLabel() {
            return this.hasIncludedLabel("09_finalização_de_dados") || this.hasIncludedLabel("10_assinatura_de_contrato") || this.hasIncludedLabel("11_matrícula") || this.hasIncludedLabel("12_contrato_recusado") || this.hasIncludedLabel("13_matrícula_manual") || this.hasIncludedLabel("14_contrato_parcial");
        },
        hasIncludedLabel(label) {
            return this.selectedSchoolLabels.includes(label);
        },
        async fetchLabels() {
            if (!this.contact.id) {
                return;
            }
            this.$store.dispatch('contactLabels/get', this.contact.id);
        },
        async saveData() {
            this.isSavingData = true;
            await this.submitForm(false);
            this.isSavingData = false;
        },
        async generateContractAndSend() {
            this.isGeneratingContract = true;
            await this.submitForm(true);
            this.isGeneratingContract = false;
        },
        async submitForm(processContract) {
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.focusFirstErrorField();
                return;
            }

            this.showLoadingModal = true;
            const payload = this.enrollmentPayload();
            payload.generate_and_send_term = processContract;

            try {
                await this.$store.dispatch('schoolEnrollments/update', { id: this.enrollment.id, ...payload });
                const successMessage = processContract
                    ? this.$t('SCHOOL_CONTACT_FORM.FORM.GENERATE_CONTRACT.SUCCESS_MESSAGE')
                    : this.$t('SCHOOL_CONTACT_FORM.FORM.EDIT_CONTRACT.SUCCESS_MESSAGE');
                this.showAlert(successMessage);
                
                if (!processContract) {
                    this.contractSaved = true;
                } else {
                    this.onCancel();
                }
            } catch (error) {
                const errs = error?.cause || {};
                if (errs?.installment_quantity || errs?.start_date_installment || errs?.last_day_installment) {
                    const msg = errs?.installment_quantity?.[0] || errs?.start_date_installment?.[0] || errs?.last_day_installment?.[0];
                    if (msg && msg.includes('a vista')) {
                        this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.FINANCIAL_PLAN.A_VISTA_DEC_PREV_YEAR_ERROR'));
                    } else {
                        this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.FINANCIAL_PLAN.YEAR_LIMIT_ERROR'));
                    }
                } else {
                    const msg = error?.message || error?.response?.data?.message;
                    if (msg) {
                        this.showAlert(msg);
                    } else {
                        const errorMessage = processContract
                            ? this.$t('SCHOOL_CONTACT_FORM.FORM.GENERATE_CONTRACT.ERROR_MESSAGE')
                            : this.$t('SCHOOL_CONTACT_FORM.FORM.EDIT_CONTRACT.ERROR_MESSAGE');
                        this.showAlert(errorMessage);
                    }
                }
            } finally {
                this.showLoadingModal = false;
            }
        },
        
        focusFirstErrorField() {
            this.$nextTick(() => {
                // Encontra o primeiro elemento com erro na ordem do formulário
                const errorElements = this.$el.querySelectorAll('.error input, .error select, .error textarea, .error .multiselect');
                
                if (errorElements.length > 0) {
                    const firstError = errorElements[0];
                    
                    // Se for multiselect, foca no elemento correto
                    if (firstError.classList.contains('multiselect')) {
                        const input = firstError.querySelector('.multiselect__input') || firstError.querySelector('.multiselect__tags');
                        if (input) {
                            input.focus();
                        }
                    } else {
                        firstError.focus();
                    }
                    
                    // Rola até o elemento com erro
                    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    return;
                }

                // Fallback: procura por elementos com mensagens de erro próximas
                const errorMessages = this.$el.querySelectorAll('.message');
                for (const msg of errorMessages) {
                    const container = msg.closest('label') || msg.parentElement;
                    if (container) {
                        const field = container.querySelector('input, select, textarea, .multiselect');
                        if (field) {
                            if (field.classList.contains('multiselect')) {
                                const input = field.querySelector('.multiselect__input') || field.querySelector('.multiselect__tags');
                                if (input) input.focus();
                            } else {
                                field.focus();
                            }
                            field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            break;
                        }
                    }
                }
            });
        },

        checkEnrollment(selectedEnrollment) {
            const currentSchool = this.currentAccount?.school_accounts?.find(school_account => school_account?.school_document === selectedEnrollment?.enrollment_school_document);
            this.currentSchoolData = currentSchool;
            this.contractType = 'Digital';
            this.contractSaved = false;
            this.chooseEnrollment(selectedEnrollment, currentSchool);
        },

        chooseEnrollment(selectedEnrollment, currentSchool) {
            this.isPopulatingData = true;
            const { school_contract } = selectedEnrollment;
            this.contractFiles = school_contract?.contract_files || [];

            this.contractSaved = false;

            const section = currentSchool?.school_class_rules?.find(c => c.school_class === selectedEnrollment?.school_class);
            
            this.contractorName = school_contract?.contractor_name || selectedEnrollment?.responsible_name || '';
            this.contractorAddress = school_contract?.contractor_address || 
                (selectedEnrollment?.responsible_street + ", " + selectedEnrollment?.responsible_building_number) || '';

            this.contractorDistrict = school_contract?.contractor_district || selectedEnrollment?.responsible_district || '';
            this.contractorState = this.getStateById(school_contract?.contractor_state || selectedEnrollment?.responsible_state) || '';
            this.contractorCity = school_contract?.contractor_city || selectedEnrollment?.responsible_city || '';
            this.contractorZipCode = school_contract?.contractor_zip_code || selectedEnrollment?.responsible_zip_code|| '';
            this.contractorPhoneNumber = school_contract?.contractor_phone_number || selectedEnrollment?.responsible_phone_number || '';
            this.contractorEmail = school_contract?.contractor_email || selectedEnrollment?.responsible_email || '';
            this.contractorFirstDocument = school_contract?.contractor_first_document || selectedEnrollment?.responsible_first_document || '';
            this.contractorSecondDocument = school_contract?.contractor_second_document || '';
            this.contractorBirthDate = selectedEnrollment?.responsible_birthday || school_contract?.contractor_birth_date || '';
            this.contractorOccupation = school_contract?.contractor_occupation || '';
            this.contractorMaritalStatus = school_contract?.contractor_marital_status || '';

            this.studentSchoolClass = school_contract?.student_school_class || selectedEnrollment?.school_class || '';
            this.studentSchoolingType = this.$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.OPTIONS').find(option => option.VALUE === school_contract?.student_schooling_type) || '';
            this.studentDateOfBirth = school_contract?.student_date_of_birth || '';
            this.studentName = school_contract?.contract_student_name || selectedEnrollment?.student_name || '';
            this.studentFirstDocument = school_contract?.contract_student_first_document || selectedEnrollment?.student_first_document || '';

            this.installmentMaturityOptions = currentSchool?.installments_maturity_options || [5,10,15,20];
            this.refreshInstallmentsQuantityOptions();

            this.schoolCity = school_contract?.school_city || currentSchool?.school_city || '';
            this.schoolEmail = school_contract?.school_email || currentSchool?.school_email || '';
            this.schoolDocument = school_contract?.school_document || currentSchool?.school_document || selectedEnrollment?.enrollment_school_document || '';
            this.schoolName = school_contract?.school_name || currentSchool?.school_name || '';
            this.schoolCorporateName = school_contract?.school_corporate_name || currentSchool?.school_corporate_name || '';
            this.schoolDistrict = school_contract?.school_district || currentSchool?.school_district || '';
            this.schoolPhoneNumber = school_contract?.school_phone_number || currentSchool?.school_phone_number || '';
            this.schoolAddress = school_contract?.school_address || currentSchool?.school_address || '';
            this.schoolZipCode = school_contract?.school_zip_code || currentSchool?.school_zip_code || '';
            this.schoolState = this.getStateById(school_contract?.school_state) || this.getStateById(currentSchool?.school_state) || '';

            this.annuity = (school_contract?.annuity && parseFloat(school_contract.annuity).toFixed(2)) || (section?.contract_annuity && parseFloat(section?.contract_annuity).toFixed(2)) || '';
            this.installmentsMaturity = school_contract?.installments_maturity || '';

            this.punctualityDiscountPercentage = school_contract?.punctuality_discount_percentage || section?.contract_punctuality_discount_percentage || '';
            this.punctualityDiscountValue = (school_contract?.punctuality_discount_value && parseFloat(school_contract?.punctuality_discount_value).toFixed(2)) || (section?.contract_punctuality_discount_value && parseFloat(section?.contract_punctuality_discount_value).toFixed(2)) || '';
            this.punctualityDiscountIsPercentage = !!this.punctualityDiscountPercentage; 

            this.contractPunctualityDiscountRule = school_contract?.contract_punctuality_discount_rule || '';
            this.punctualityDiscountRuleEqualToDueDate = (school_contract?.contract_punctuality_discount_rule === school_contract?.start_date_installment);

            this.withdrawalFeePercentage = school_contract?.withdrawal_fee_percentage || section?.contract_withdrawal_fee_percentage || '';
            this.withdrawalFeeValue = (school_contract?.withdrawal_fee_value && parseFloat(school_contract?.withdrawal_fee_value).toFixed(2)) || (section?.contract_withdrawal_fee_value && parseFloat(section?.contract_withdrawal_fee_value).toFixed(2)) || '';
            this.withdrawalFeeIsPercentage = !!this.withdrawalFeePercentage; 

            this.installmentsQuantity = school_contract?.installment_quantity || section?.contract_installment_number || '';
            this.installmentValue = (school_contract?.installment_value && parseFloat(school_contract?.installment_value).toFixed(2)) || (section?.contract_installment_value && parseFloat(section?.contract_installment_value).toFixed(2)) || '';

            this.remainingInstallmentsMaturity = school_contract?.remaining_installments_maturity || '';
            this.startDateInstallment = school_contract?.start_date_installment ? school_contract.start_date_installment.substring(0, 7) : '';
            this.lastDayInstallment = school_contract?.last_day_installment || '';
            // refresh options after dates are set
            this.refreshInstallmentsQuantityOptions();

            this.studentCustodyArrangement = this.$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_CUSTODY_ARRANGEMENT.OPTIONS').find(
                option => option.NAME === school_contract?.student_custody_arrangement
            ) || '';

            this.relationshipWithStudent = this.$t('SCHOOL_CONTACT_FORM.FORM.RELATIONSHIP_WITH_STUDENT.OPTIONS')
                .find(option => option.NAME === school_contract?.relationship_with_student) || '';

            const maritalOption = this.$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.OPTIONS')
                .find(option => option.NAME === school_contract?.student_parents_marital_status) || '';

            if (maritalOption) {
                this.studentParentsMaritalStatus = maritalOption;
                this.studentParentsMaritalStatusOther = '';
            }
            else if (school_contract?.student_parents_marital_status) {
                this.studentParentsMaritalStatus = this.$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_PARENTS_MARITAL_STATUS.OPTIONS')
                    .find(option => option.ID === "OTHER");
                this.studentParentsMaritalStatusOther = school_contract.student_parents_marital_status;
            } else {
                this.studentParentsMaritalStatus = '';
                this.studentParentsMaritalStatusOther = '';
            }

            if(selectedEnrollment?.extension_session_enable && currentSchool?.extension_session_option_enable){
                this.extensionSessionEnable = true;
                this.extensionSessionAbsenceCutoffDayForMealDiscount = school_contract?.extension_session_absence_cutoff_day_for_meal_discount;
                this.extensionSessionAnnuity = school_contract?.extension_session_annuity && parseFloat(school_contract?.extension_session_annuity).toFixed(2);;
                this.extensionSessionChangeDeadline = school_contract?.extension_session_change_deadline;
                this.extensionSessionEnd = school_contract?.extension_session_end;
                this.extensionSessionInstallmentValue = school_contract?.extension_session_installment_value && parseFloat(school_contract?.extension_session_installment_value).toFixed(2);;
                this.extensionSessionMeals = school_contract?.extension_session_meals;
                this.extensionSessionMinAbsenceDaysForMealDiscount = school_contract?.extension_session_min_absence_days_for_meal_discount;
                this.extensionSessionMinimumMealDays = school_contract?.extension_session_minimum_meal_days;
                this.extensionSessionShift = school_contract?.extension_session_shift;
                this.extensionSessionStart = school_contract?.extension_session_start;
                this.extensionSessionWeekdays = school_contract?.extension_session_weekdays;
            }

            this.$emit('enrollment-selected', selectedEnrollment.id);

            this.$nextTick(() => {
                this.isPopulatingData = false;
            });
        },
        refreshInstallmentsQuantityOptions() {
            if (!this.enrollment) return;
            const allowed = this.allowedInstallmentsByStartDate;
            const remainingMonthsArray = Array.from({ length: allowed }, (_, i) => i + 1);
            this.installmentsQuantityOptions = remainingMonthsArray;
        },
        addWitness() {

        },
        removeWitness(index) {
        },
        async handleAttachFile(event) {
            if (this.isEditingBlocked) {
                if (event?.target) {
                    event.target.value = '';
                }
                return;
            }

            const [file] = event.target.files;

            const payload = {
                id: this.enrollment.id, 
                contractFile: file,
                contactId: this.contact.id, 
            }

            try {
                await this.$store.dispatch('schoolEnrollments/addContractFile', payload);
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.ADD_CONTRACT_FILE.SUCCESS_MESSAGE'));
                this.contractFiles = this.currentEnrollment(this.enrollment.id).school_contract.contract_files;
            } catch (error) {
                if(error.cause.contract_file){
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.ADD_CONTRACT_FILE.INVALID_FILE_ERROR_MESSAGE'));
                } else if (error.cause.contract_files) {
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.ADD_CONTRACT_FILE.FILE_LIMIT_ERROR_MESSAGE'));
                } else {
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.ADD_CONTRACT_FILE.ERROR_MESSAGE'));
                    }
                }
            },
            openFileInput() {
                if (this.isEditingBlocked) {
                    return;
                }

                this.$refs.fileInput.click();
            },
            async removeFile(contractFileId) {
                if (this.isEditingBlocked) {
                    return;
                }

                const payload = {
                    id: this.enrollment.id, 
                    contactId: this.contact.id,
                    contract_file_id: contractFileId, 
                }
                try {
                    await this.$store.dispatch('schoolEnrollments/removeContractFile', payload);
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.REMOVE_CONTRACT_FILE.SUCCESS_MESSAGE'));
                    this.contractFiles = this.contractFiles.filter(contractFile => contractFile.id !== contractFileId);
                } catch (error) {
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.REMOVE_CONTRACT_FILE.ERROR_MESSAGE'));
                }
            },
            validateMoneyGreaterThanZero(moneyValue) {
                if (this.moneyMaskToDecimal(moneyValue) <= 0) {
                    return false 
                } 
                return true;
            },
            roundMoneyToFloor(value) {
                return (Math.floor(value * 100) / 100).toFixed(2);
            },
            async searchContractorZipCode() {
                try {
                    const response = await this.fetchZipCode(this.contractorZipCode);
                    const {data} = response;

                    this.contractorState = this.$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS').find(option => option.ID === data.uf);
                    this.contractorCity = data.localidade;
                    this.contractorDistrict = data.bairro;
                    this.contractorAddress = data.logradouro;
                } catch (error) {
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_ZIPCODE.API_ERROR'));
                }
            },
            fixDateMaturityDay(value) {
                if (!value) return '';
                const date = new Date(value);
                if (Number.isNaN(date.getTime())) return '';
                const year = date.getUTCFullYear();
                const month = date.getUTCMonth();
                const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
                const maturityDay = this.remainingInstallmentsMaturity ? parseInt(this.remainingInstallmentsMaturity, 10) : date.getUTCDate();
                return `${year}-${String(month + 1).padStart(2, '0')}-${String(Math.min(maturityDay, lastDayOfMonth)).padStart(2, '0')}`;
            },
            updateLastDayInstallment() {
                if (!this.startDateInstallment || !this.installmentsQuantity) return;

                const startDate = new Date(this.startDateInstallment);
                const targetMonth = startDate.getUTCMonth() + (this.installmentsQuantity - 1);
                const targetYear = startDate.getUTCFullYear() + Math.floor(targetMonth / 12);
                let calculatedMonth = targetMonth % 12;

                if (this.selectedEnrollmentYear) {
                    const enrollmentYear = parseInt(this.selectedEnrollmentYear, 10);
                    const isExtended = this.currentSchoolData?.allow_installments_after_selected_year;
                    const maxAllowedYear = isExtended ? enrollmentYear + 1 : enrollmentYear;
                    const maxAllowedMonth = isExtended ? 0 : 11;

                    if (targetYear > maxAllowedYear || (targetYear === maxAllowedYear && calculatedMonth > maxAllowedMonth)) {
                        calculatedMonth = maxAllowedMonth;
                        const adjustedYear = maxAllowedYear;
                        this.lastDayInstallment = this.fixDateMaturityDay(new Date(Date.UTC(adjustedYear, calculatedMonth, 1)));
                        return;
                    }
                }

                this.lastDayInstallment = this.fixDateMaturityDay(new Date(Date.UTC(targetYear, calculatedMonth, 1)));
            }
    }
}

</script>
<styles scoped lang="scss">

.contact--form {
    padding: var(--space-normal) var(--space-large) var(--space-large);
    min-height: 200px;

    .columns {
        padding: 0 var(--space-smaller);
    }
}

.add-button-area {
    display: flex;
    align-items: center;
    padding-left: 5px;
    padding-bottom: 5px;
}

.add-button {
    height: 25px;
}

.enrollment-title {
    margin-bottom: 5px;
}

/* New layout helpers */
.form-section {
    @apply bg-white dark:bg-slate-800 rounded-lg border border-solid border-slate-100 dark:border-slate-700 p-4 mb-4;
}

.section-title {
    @apply text-slate-800 dark:text-slate-100 font-semibold text-xs md:text-sm mb-3 flex items-center gap-2 uppercase tracking-wide;
}

.grid-2 {
    @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.grid-3 {
    @apply grid grid-cols-1 md:grid-cols-3 gap-4;
}

.label-text {
    @apply text-slate-700 dark:text-slate-200 text-sm font-medium;
}

.filters-wrap {
  padding: var(--space-normal);
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--color-border);
  margin-bottom: var(--space-normal);
  background: var(--white);
  @apply dark:bg-slate-900 dark:border-slate-600
}

.filter-add {
  margin-top: var(--space-normal);
  margin-bottom: var(--space-normal);
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-delete {
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter {
  background: var(--w-25);
  padding: var(--space-small);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  margin-top: var(--space-smaller);
  @apply dark:bg-slate-800 dark:border-slate-600
}

.filter.error {
  background: var(--r-50);
}

.filter-inputs {
  display: flex;
}

.filter-none {
    display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: var(--space-normal);
  font-weight: bold;
}

.filter-title {
    display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.file-section-empty-label {
    display: flex;
    flex-direction: column;
    align-items: center;
  justify-content: center;
  font-weight: bold;
}

.file-section-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius-medium);
    padding: var(--space-small) ;
    background: var(--white);
    gap: var(--space-small);
    @apply dark:bg-slate-900 dark:border-slate-600
}

.file-section-item {
    background: var(--w-25);
    padding: var(--space-small);
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius-medium);
    @apply dark:bg-slate-800 dark:border-slate-600
}

.file-section-upload-button {
    margin-top: var(--space-smaller);
}

/* Sticky footer actions for long forms */
.modal-footer {
    @apply sticky bottom-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-t border-solid border-slate-100 dark:border-slate-700 mt-2 pt-3 pb-3;
}

.field-button {
    margin-top: -10px;
    display: flex;
    justify-content: end;
}

.loading-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
}
.loading-content {
    background-color: white;
    padding: 20px;
    border-radius: 5px;
    text-align: center;
}
.spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border-left-color: #09f;
    animation: spin 1s ease infinite;
    margin: 0 auto 10px;
}
@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
.year-badge {
    @apply py-0.5 px-1.5 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0;
    &--0 { @apply bg-woot-100 text-woot-700 dark:bg-woot-700 dark:text-woot-100; }
    &--1 { @apply bg-green-100 text-green-700 dark:bg-green-700 dark:text-green-100; }
    &--2 { @apply bg-yellow-100 text-yellow-700 dark:bg-yellow-700 dark:text-yellow-100; }
    &--3 { @apply bg-red-100 text-red-700 dark:bg-red-700 dark:text-red-100; }
    &--4 { @apply bg-violet-100 text-violet-700 dark:bg-violet-700 dark:text-violet-100; }
}
.alert-message {
    @apply flex items-center gap-2 p-3 rounded-md;
    &.warning {
        @apply bg-yellow-100 dark:bg-yellow-800 border border-solid border-yellow-200 dark:border-yellow-700;
    }
    .text-sm {
        @apply text-yellow-800 dark:text-yellow-100;
    }
}

</styles>